import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { useIsMutating, useQueryClient } from "react-query";

import SegmentControl from "../../Components/Buttons/segment-control";
import DiscountCode from "../../Components/Form Fields/discount-field";
import PrimaryButton from "../../Components/Buttons/primary-button";
import { useCreditBuilderDealAction, useStakeCheckingDealAction, useConsumerDealsData } from "../Deals";

import renderManagePaymentMethods from "../../Organisms/Modals/Payment Methods/manage-payment-methods";
import renderWelcomeToStakeSaver from "../../Organisms/Modals/Stake Saver/welcome-to-stake-saver";
import renderAlertConfirmationModal from "../../Organisms/Modals/alert-confirmation-modal";
import renderStakeSaverSetupSuccess from "../../Organisms/Modals/Stake Saver/stake-saver-setup-success";

import { useConsumerPaymentMethods, useDeals } from "../../CustomHooks/Queries";
import { useApplyStakeSaverDiscount, useCancelStakeSaver, useJoinStakeSaver } from "../../CustomHooks/Mutations";

import { useModalContext } from "../../Contexts/ModalContext";
import { useAppUtilityContext } from "../../Contexts/AppUtilityContext";

import { dispatchAPIErrorToast, dispatchInfoToast, formatCurrency, getUserId, zPad } from "../../Utils/helpers";
import { APIQueryKeys, CancelStakeSaverDealModalProps } from "../../Utils/constants";

import {
  ArrowLeftCircle,
  CardFilled,
  CheckCircleOutline,
  ChevronRight,
  CircleExclamationOutlined,
  CrossMark,
  Discount,
  Wallet,
} from "../../Assets/Icons";

import "./stake-saver.scss";
import "./stake-saver-responsive.scss";

const StakeSaverBundleDetails = ({ offer, filteredFrequency, setDiscountCode }) => {
  const [isCompleteDiscountCode, setIsCompleteDiscountCode] = useState(false);
  const [appliedDiscountCode, setAppliedDiscountCode] = useState("");
  const [discountError, setDiscountError] = useState(null);
  const [paymentFrequency, setPaymentFrequency] = useState(null);

  const {
    isStakeSaverActivated,
    formattedBundleValue,
    formattedSubTotal,
    formattedDiscount,
    formattedTotal,
    formattedType,
  } = useMemo(() => {
    const isStakeSaverActivated = offer?.is_activated;

    const formattedBundleValue = formatCurrency(paymentFrequency?.amount ?? 0);
    const formattedDiscount = !!paymentFrequency?.discount ? formatCurrency(paymentFrequency?.discount * -1) : null;
    const formattedSubTotal = formatCurrency(paymentFrequency?.sub_total ?? 0);
    const formattedTotal = formatCurrency(paymentFrequency?.total ?? 0);
    const formattedType = paymentFrequency?.type?.toLowerCase();
    return {
      isStakeSaverActivated,
      formattedBundleValue,
      formattedSubTotal,
      formattedDiscount,
      formattedTotal,
      formattedType,
    };
  }, [offer, paymentFrequency]);

  const {
    isLoading: isApplyingDiscount,
    mutate: ApplyDiscount,
    reset: ResetApplyDiscount,
    isSuccess: isDiscountApplied,
    data: discountAppliedData,
  } = useApplyStakeSaverDiscount({
    onSuccess: () => {
      setDiscountCode(appliedDiscountCode);
      setDiscountError(null);
    },
    onError: (err) => setDiscountError(err?.response?.data?.error ?? "Oops... Something went wrong"),
  });

  useEffect(() => {
    setPaymentFrequency(
      offer?.is_activated
        ? offer?.check_out_details?.activatedFrequency?.[0]
        : isDiscountApplied
        ? discountAppliedData?.data?.data
        : filteredFrequency
    );
  }, [offer, filteredFrequency, isDiscountApplied, discountAppliedData]);

  useEffect(() => {
    if (isCompleteDiscountCode) {
      ApplyDiscount({
        mobile_user_id: getUserId(),
        discount_code: appliedDiscountCode,
        frequency: filteredFrequency?.type?.toLowerCase(),
      });
    }
    //Add only the dependancies on which the API request should be made
    //eslint-disable-next-line
  }, [isCompleteDiscountCode, appliedDiscountCode, filteredFrequency?.type]);

  const handleApply = () => {
    setIsCompleteDiscountCode(true);
    ApplyDiscount({
      mobile_user_id: getUserId(),
      discount_code: appliedDiscountCode,
      frequency: formattedType,
    });
  };

  const handleRemoveDiscount = () => {
    setIsCompleteDiscountCode(false);
    setAppliedDiscountCode("");
    setDiscountCode(null);
    ResetApplyDiscount();
  };

  return (
    <>
      <div
        className={`offer-items ${!paymentFrequency ? "no-aggregation" : ""} ${
          !offer?.is_activated ? "has-pricing-reminder" : ""
        }`}
      >
        <div className="offer-item">
          <div className="offer-specification">
            <div className="offer-header">
              <div className="offer-name">{offer?.offer_name}</div>
              {!!paymentFrequency && (
                <div className="offer-fee">
                  {formattedBundleValue}
                  <span className="offer-term">/{formattedType}</span>
                </div>
              )}
            </div>
            <ul className="offer-pointers">
              {offer?.offer_details?.learn_more_items?.map((benefit, idx) => (
                <li key={idx}>{benefit}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      {!offer?.is_activated && (
        <div className="pricing-reminder-section">
          <div className="pricing-reminder">
            <p className="pricing-reminder-intro">
              Reminder, Stake is free as long as you do at least one of the following each month:
            </p>
            <ul className="offer-pointers">
              <li>Pay rent with Stake</li>
              <li>OR: Make 5 purchases on Stake Debit</li>
              <li>OR: Receive $250+ via direct deposit or recurring transfer</li>
            </ul>
            <p className="pricing-reminder-footer">Enter code STAKESAVER3 below for 3 months free to try it out.</p>
          </div>
        </div>
      )}
      {!!paymentFrequency && (
        <div className="aggregation">
          {!isStakeSaverActivated && !isDiscountApplied && (
            <DiscountCode
              className="mb-3"
              onApply={handleApply}
              value={appliedDiscountCode}
              onChange={(e) => {
                setIsCompleteDiscountCode(false);
                setAppliedDiscountCode(e.target.value);
              }}
              isApplying={isApplyingDiscount}
              errorMessage={discountError}
            />
          )}
          <div className="fee">
            <div className="fee-name">Subtotal</div>
            <div className="fee-value">{formattedSubTotal}</div>
          </div>
          {!!formattedDiscount && (
            <div className="fee">
              <div className="discount-row">
                <div className="fee-name">Discount</div>
                {!isStakeSaverActivated && isDiscountApplied && (
                  <span className="discount-code-chip">
                    <Discount className="discount-icon" />
                    {appliedDiscountCode}
                    <button className="remove" onClick={handleRemoveDiscount}>
                      <CrossMark />
                    </button>
                  </span>
                )}
              </div>
              <div className="fee-value">{formattedDiscount}</div>
            </div>
          )}
          <div className="total-fee">
            <div className="fee-name">Total</div>
            <div className="fee-value">{formattedTotal}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default function StakeSaver() {
  const [paymentFrequency, setPaymentFrequency] = useState(null);

  const modalContext = useModalContext();
  const { onModalClose } = modalContext;

  const { setAppUtilityProps } = useAppUtilityContext();

  const navigate = useNavigate();
  const location = useLocation();
  const fromWelcome = location.state?.fromWelcome || false;
  const welcomeFlowData = location.state || {};

  // Clear welcome flow flag when component unmounts or user navigates away
  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && window.sessionStorage) {
        window.sessionStorage.removeItem("fromWelcomeFlow");
      }
    };
  }, []);

  const {
    isLoadingConsumerDealsData,
    stakeSaverOffer,
    stakeCheckingOffer,
    creditBuilderOffer,
    isStakeCheckingActive,
    isCreditBuilderActive,
    consumerDealsAPI,
  } = useConsumerDealsData();

  const { isLoading: isLoadingPaymentMethods, data: paymentMethods } = useConsumerPaymentMethods();

  const [discountCode, setDiscountCode] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [selectedBackupPaymentMethod, setSelectedBackupPaymentMethod] = useState(null);

  const { data: deals } = useDeals();

  // When coming from welcome flow, force checkout mode even if offer is activated
  // This ensures discount code functionality is available
  const isActivated = stakeSaverOffer?.is_activated;

  useEffect(() => {
    setSelectedPaymentMethod((p) =>
      isActivated
        ? stakeSaverOffer?.check_out_details?.selected_payment_method
        : !!p
        ? p
        : paymentMethods?.data?.data?.[0] ?? null
    );
    setSelectedBackupPaymentMethod(
      isActivated ? stakeSaverOffer?.check_out_details?.selected_backup_payment_method : null
    );
  }, [isActivated, paymentMethods, stakeSaverOffer]);

  const stakeSaverFrequencies = useMemo(
    () =>
      stakeSaverOffer?.check_out_details?.frequency?.map((feeOption) => ({
        ...feeOption,
        label: feeOption.type,
        value: feeOption.type,
      })) ?? [],
    [stakeSaverOffer]
  );

  const filteredFrequency = useMemo(() => {
    if (!paymentFrequency) {
      setPaymentFrequency(stakeSaverFrequencies?.[0]?.type);
    }
    return stakeSaverFrequencies?.find((feeOption) => feeOption.type === paymentFrequency);
  }, [stakeSaverFrequencies, paymentFrequency]);

  const StakeCheckingDealAction = useStakeCheckingDealAction(isStakeCheckingActive);
  const CreditBuilderDealAction = useCreditBuilderDealAction(isCreditBuilderActive);

  const stakeSaverAddOns = useMemo(
    () => [
      ...(!!stakeCheckingOffer ? [{ ...stakeCheckingOffer, onClick: StakeCheckingDealAction }] : []),
      ...(!!creditBuilderOffer ? [{ ...creditBuilderOffer, onClick: CreditBuilderDealAction }] : []),
    ],
    [stakeCheckingOffer, creditBuilderOffer, StakeCheckingDealAction, CreditBuilderDealAction]
  );

  const queryClient = useQueryClient();

  const { isLoading: isJoiningStakeSaver, mutate: JoinStakeSaver } = useJoinStakeSaver({
    onSuccess: async ({ data }) => {
      if (fromWelcome) {
        // Coming from welcome flow - API call completed successfully
        if (data?.data?.show_alert_message) {
          dispatchInfoToast(data?.data?.alert_message, { key: "joinStakeSaver" });
        }

        // Wait 5 seconds for backend processing
        await new Promise((resolve) => setTimeout(resolve, 5000));

        // Invalidate and refetch all queries that the banking page depends on
        // This ensures all data is ready before showing the success modal and navigating
        await Promise.all([
          queryClient.invalidateQueries(APIQueryKeys.consumerDeals),
          queryClient.invalidateQueries(APIQueryKeys.deals),
          queryClient.invalidateQueries(APIQueryKeys.userAccount),
          queryClient.invalidateQueries(APIQueryKeys.activities),
          queryClient.invalidateQueries(APIQueryKeys.connectedAccounts),
          queryClient.invalidateQueries(APIQueryKeys.stakeDebit),
          queryClient.invalidateQueries(APIQueryKeys.astraCards),
          queryClient.invalidateQueries(APIQueryKeys.payrollAccount),
        ]);

        // Refetch all queries to ensure data is loaded before navigation
        await Promise.all([
          queryClient.refetchQueries(APIQueryKeys.consumerDeals),
          queryClient.refetchQueries(APIQueryKeys.deals),
          queryClient.refetchQueries(APIQueryKeys.userAccount),
          queryClient.refetchQueries(APIQueryKeys.activities),
          queryClient.refetchQueries(APIQueryKeys.connectedAccounts),
          queryClient.refetchQueries(APIQueryKeys.stakeDebit),
          queryClient.refetchQueries(APIQueryKeys.astraCards),
          queryClient.refetchQueries(APIQueryKeys.payrollAccount),
        ]);

        // Show success modal and navigate to banking page with fromWelcome=true for pulsing animation
        // All data should now be loaded, so the banking page won't show 404
        renderStakeSaverSetupSuccess(modalContext);
        navigate("/banking?fromWelcome=true");
      } else {
        renderWelcomeToStakeSaver(modalContext, stakeSaverOffer);
        navigate("/deals/setup-stake-saver");
      }
    },
    onError: (err) => dispatchAPIErrorToast({ message: err?.response?.data?.error, key: "joinStakeSaver" }),
  });

  const { isLoading: isCancelingStakeSaver, mutate: CancelStakeSaver } = useCancelStakeSaver({
    onSuccess: async (cancelStakeSaverResponse) => {
      const consumerDealsResponse = await consumerDealsAPI.refetch();
      if (consumerDealsResponse.isSuccess) {
        const hasAtleastOneOffer = (consumerDealsResponse?.data?.data?.data?.offers?.length ?? 0) > 0;

        if (cancelStakeSaverResponse?.data?.data?.is_individual_signup || hasAtleastOneOffer) {
          navigate("/deals");
        } else {
          setAppUtilityProps((p) => ({
            ...p,
            hideDealsRoutes: !hasAtleastOneOffer,
          }));
          navigate("/home");
        }
      }
    },
    onError: (err) => dispatchAPIErrorToast({ message: err?.response?.data?.error, key: "cancelStakeSaver" }),
  });

  const handleChangePaymentMethod = (type) =>
    renderManagePaymentMethods(modalContext, {
      selectedPaymentMethod: type === "primary" ? selectedPaymentMethod : selectedBackupPaymentMethod,
      isBackupPayment: type === "backup",
      ...(!isActivated
        ? {
            onChange: (selectedPaymentMethod) => {
              onModalClose();
              if (type === "primary") setSelectedPaymentMethod(selectedPaymentMethod);
              else setSelectedBackupPaymentMethod(selectedPaymentMethod);
            },
          }
        : {
            mustUpdatePaymentMethod: true,
            chargePaymentImmediately: false,
            dealId: stakeSaverOffer?.id,
          }),
    });

  const isWalletOrStakeChecking = selectedPaymentMethod?.is_stake_checking || selectedPaymentMethod?.is_wallet;

  const handleStakeSaverCTAClick = () => {
    if (isActivated) {
      renderAlertConfirmationModal(modalContext, {
        ...CancelStakeSaverDealModalProps,
        onCTAButtonClick: () => {
          onModalClose();
          CancelStakeSaver(deals?.data?.data?.currentDeal?.sfid);
        },
        onSecondaryCTAButtonClick: onModalClose,
      });
    } else {
      const payload = {
        consumer_sfid: deals?.data?.data?.currentDeal?.sfid,
        frequency: filteredFrequency?.type,
        amount: filteredFrequency?.amount,
        payment_method: selectedPaymentMethod?.is_wallet
          ? "Wallet"
          : selectedPaymentMethod?.is_stake_checking
          ? "Stake Checking"
          : "Card",
        discount_code: discountCode,
        ...(selectedPaymentMethod?.is_card
          ? {
              card_details: {
                card_id: selectedPaymentMethod?.card_id,
              },
            }
          : { backup_card_id: selectedBackupPaymentMethod?.card_id }),
      };

      // Include occupation and other data from welcome flow if available
      if (fromWelcome && welcomeFlowData.occupation) {
        payload.occupation = welcomeFlowData.occupation;
      }
      if (fromWelcome && welcomeFlowData.address) {
        payload.address1 = welcomeFlowData.address;
        if (welcomeFlowData.city) payload.city = welcomeFlowData.city;
        if (welcomeFlowData.state) payload.state = welcomeFlowData.state;
        if (welcomeFlowData.zipCode) payload.zipcode = welcomeFlowData.zipCode;
      }
      if (fromWelcome && welcomeFlowData.ssn) {
        payload.ssn = welcomeFlowData.ssn.replace(/\D/g, ""); // Remove non-digits
      }
      if (fromWelcome && welcomeFlowData.dob) {
        payload.dob = welcomeFlowData.dob;
      }

      // Log payload for debugging
      console.log("join_stake_saver payload:", payload);
      console.log("welcomeFlowData:", welcomeFlowData);
      console.log("fromWelcome:", fromWelcome);

      JoinStakeSaver(payload);
    }
  };

  const PaymentMethodIcon = isWalletOrStakeChecking ? Wallet : CardFilled;
  const BackupPaymentStatusIcon = !selectedBackupPaymentMethod ? CircleExclamationOutlined : CheckCircleOutline;

  const isLoading = isLoadingConsumerDealsData || isLoadingPaymentMethods;

  const isApplyingDiscount = useIsMutating(APIQueryKeys.applyingStakeSaverDiscount);

  return (
    <div className="stake-saver">
      <section className={isActivated ? "card-section-wrapper" : ""}>
        <div className="max-width-container justify-content-between">
          {isLoading ? (
            <div className="loading-content">
              <CircularProgress />
            </div>
          ) : (
            <div
              className={`d-flex flex-grow-1 flex-column justify-content-between${isActivated ? " card-section" : ""}`}
            >
              <div>
                <div className={`section-header ${isActivated ? "" : " justify-content-start enlarged"}`}>
                  <button className="go-back-button" onClick={() => navigate(-1)}>
                    <ArrowLeftCircle />
                  </button>
                  <h3 className="section-title">{isActivated ? "Settings" : "Checkout"}</h3>
                  {isActivated && <div className="dummy"></div>}
                </div>
                {!isActivated && stakeSaverFrequencies.length > 0 && (
                  <SegmentControl
                    className="mb-5"
                    value={paymentFrequency}
                    onChange={(_, value) => setPaymentFrequency(value)}
                    exclusive
                    segmentOptions={stakeSaverFrequencies}
                  />
                )}
                <StakeSaverBundleDetails
                  offer={stakeSaverOffer}
                  filteredFrequency={filteredFrequency}
                  setDiscountCode={setDiscountCode}
                />
                {isActivated && stakeSaverAddOns.length > 0 && (
                  <div className="offer-add-ons mt-4">
                    {stakeSaverAddOns.map((addOn, idx) => (
                      <button key={idx} className="add-on" onClick={addOn.onClick}>
                        <span className="add-on-info">
                          <span className="title">{addOn.title}</span>
                          <span className="description">{addOn.description}</span>
                        </span>
                        <ChevronRight className="chevron-icon" />
                      </button>
                    ))}
                  </div>
                )}
                <div className="payment-method-selectable mt-5">
                  <p className="title">Payment Method</p>
                  <button className="selected-method-button" onClick={handleChangePaymentMethod.bind(this, "primary")}>
                    {!!selectedPaymentMethod && (
                      <div className="method-icon-wrapper">
                        <PaymentMethodIcon className="method-icon" />
                      </div>
                    )}
                    <div className="info">
                      <div className="method-name">
                        {!selectedPaymentMethod
                          ? "Select a Payment Method"
                          : isWalletOrStakeChecking
                          ? `Stake ${selectedPaymentMethod?.is_stake_checking ? "Checking" : "Wallet"}`
                          : selectedPaymentMethod?.card_type}
                      </div>
                      {!!selectedPaymentMethod && (
                        <div className="method-detail">
                          {isWalletOrStakeChecking
                            ? formatCurrency(selectedPaymentMethod?.current_balance ?? 0)
                            : `••••${selectedPaymentMethod?.card_last_4_digits}, exp ${zPad(
                                selectedPaymentMethod?.exp_month,
                                2
                              )}/${zPad(selectedPaymentMethod?.exp_year % 100, 2)}`}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="chevron-icon" />
                  </button>
                  {isWalletOrStakeChecking && (
                    <>
                      <div className="backup-card-status-card mt-4">
                        <BackupPaymentStatusIcon className="status-icon" />
                        <div className="backup-card-status">
                          <div className="title">
                            Backup payment method {!selectedBackupPaymentMethod ? "required" : "added"}
                          </div>
                          {!selectedBackupPaymentMethod && (
                            <>
                              <div className="description mt-1">
                                We need a backup payment method, just in case. Most Stakers never pay. Cancel anytime.
                              </div>
                              <button className="action mt-4" onClick={handleChangePaymentMethod.bind(this, "backup")}>
                                Add Backup Card
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {!!selectedBackupPaymentMethod && (
                        <button
                          className="selected-method-button"
                          onClick={handleChangePaymentMethod.bind(this, "backup")}
                        >
                          <div className="method-icon-wrapper">
                            <CardFilled className="method-icon" />
                          </div>
                          <div className="info">
                            <div className="method-name">{selectedBackupPaymentMethod?.card_type}</div>
                            {!!selectedBackupPaymentMethod && (
                              <div className="method-detail">
                                {`••••${selectedBackupPaymentMethod?.card_last_4_digits}, exp ${zPad(
                                  selectedBackupPaymentMethod?.exp_month,
                                  2
                                )}/${zPad(selectedBackupPaymentMethod?.exp_year % 100, 2)}`}
                              </div>
                            )}
                          </div>
                          <ChevronRight className="chevron-icon" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="action-container flex-column mt-8">
                <PrimaryButton
                  className={isActivated ? "red-theme" : ""}
                  onClick={handleStakeSaverCTAClick}
                  disabled={
                    isActivated
                      ? isCancelingStakeSaver
                      : !selectedPaymentMethod ||
                        (isWalletOrStakeChecking && !selectedBackupPaymentMethod) ||
                        isApplyingDiscount > 0 ||
                        isJoiningStakeSaver
                  }
                >
                  {isActivated ? "Cancel Deal" : stakeSaverOffer?.offer_details?.action_button_label}{" "}
                  {(isJoiningStakeSaver || isCancelingStakeSaver) && <CircularProgress />}
                </PrimaryButton>
                {!isActivated && (
                  <p className="text-base-medium color--mist text-center mx-auto mt-3 mb-0">
                    No hidden fees. Cancel anytime.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
